import { useMemo } from 'react'

import { type IOpportunityDetail } from '@user/store/opportunity'
import { getOpportunityStatus } from '../utils'
import { useSelectFunnelStagesByType } from '@user/store/salesFunnel'
import { useAddContact } from '@user/components/DetailPages/sections/Contacts'
import { OPPORTUNITY_STATUSES } from '@user/components/Opportunities/OpportunityCard/typings'
import { useHasScheduledAppointment } from '@user/store/scheduling/hooks'
import { ENTITY_TYPES } from '@sales-center/common'

type Props = {
  detail: IOpportunityDetail
  canUpdateOpps: boolean
}

export function useData(props: Props) {
  const {
    canUpdateOpps,
    detail: {
      id,
      name,
      company,
      salesFunnelId,
      primaryContact,
      opportunityName,
      scheduleSettings,
      salesFunnelStageId,
      externalLocationId,
      locations,
    },
  } = props

  const funnelStages = useSelectFunnelStagesByType(salesFunnelId, 'Opportunity')

  // Opportunity Status
  const status = useMemo(
    () => getOpportunityStatus(funnelStages, salesFunnelStageId).status,
    [salesFunnelStageId, funnelStages],
  )

  const entityName = name != null && name != '' && name != ' ' ? name : company

  const entityContactName = useMemo(() => {
    return company != null && company.trim().length > 0
      ? `${company} - ${opportunityName}`
      : primaryContact
        ? `${primaryContact.firstName} ${primaryContact.lastName} - ${opportunityName}`
        : `${opportunityName}`
  }, [company, opportunityName, primaryContact])

  const addContact = useAddContact()

  const opportunityMatched = Boolean(externalLocationId)

  const disableEditName = !canUpdateOpps || status !== OPPORTUNITY_STATUSES.Open

  const hasScheduledAppointment = useHasScheduledAppointment({
    entityId: id,
    entityType: ENTITY_TYPES.Opportunity,
    scheduleSettings,
  })

  return {
    ...addContact,

    status,
    entityName,
    disableEditName,
    entityContactName,
    opportunityMatched,

    funnelStages,
    hasScheduledAppointment,
    locations,
  }
}
